import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import { Expand, Delete, Arrow, Copy, Cut, Paste, Plus, Audit, CopyLink } from 'components/icons'
import { findRow, getRowValue, isColumnLockedByRules } from 'components/spreadsheet/helpers/functions'
import { hasSyncStartedThisSession } from 'components/spreadsheet/helpers/syncSession'
import { useApplicationStore } from 'hooks/application'
import { ICellValue, ITableViewGroup } from 'types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { useSearchParams } from 'react-router-dom'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { MOD_KEY, MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'

interface CellMenuProps extends MenuProps {
  onCreateRow: (values: Record<string, ICellValue>, nextRowPublicId?: string) => void
  onCreateBlankRows: (noRows: number, values?: Record<string, ICellValue>) => void
  setExpandModal: (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => void
  setRowAuditModal: (open: boolean, rowId: string) => void
  setDeleteRowModal: (deleteRowModal: boolean) => void
  multiCellSelection: boolean
}

const CellMenu: React.FC<CellMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  onCreateRow,
  onCreateBlankRows,
  setExpandModal,
  setRowAuditModal,
  setDeleteRowModal,
  multiCellSelection
}) => {
  const [, setSearchParams] = useSearchParams()
  const { spreadsheetData, selectedCell, selectedRowCount, handlePasteText, cutSelectedCells } = useDataContext()

  const rowCapabilities = getViewCapabilities(spreadsheetData.viewDetails.type).rows

  const { setSnackbarMessage } = useApplicationStore()

  // If we're in grouped mode, the rowNumber doesn't correspond to the row's position
  // in the spreadsheetData.rows[] array, so we manually search the rows array for
  // the row's position.
  // If we're inserting a new row in a group we also need to know the values for the
  // columns that are being grouped, so when we create the row it's placed in the
  // correct group.
  const getRowIndexAndValues = (useColumnName?: boolean) => {
    if (spreadsheetData.userConfiguration.groupSettings.length > 0 && menuState.rowId !== undefined) {
      const row = findRow(menuState.rowId, spreadsheetData.rows)!
      const values: Record<string, ICellValue> = {}
      for (const group of spreadsheetData.userConfiguration.groupSettings) {
        const column = spreadsheetData.viewDetails.columns.find((c) => c.publicId === group.columnId)
        if (!column) continue
        const isReadOnly =
          isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) ||
          column.scriptEnabled ||
          column.formulaEnabled ||
          column.isJoined ||
          column.isSynced
        if (isReadOnly) continue
        values[useColumnName ? group.columnName : group.columnId] = getRowValue(
          row,
          group.columnId,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.columnStore
        )
      }

      return values
    } else {
      return {}
    }
  }

  const handleInsertRowAbove = (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.preventDefault()
    const values = getRowIndexAndValues()
    onCreateRow(values, menuState.rowId)
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }
  const handleDuplicateRow = (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.preventDefault()
    if (menuState.rowId !== undefined && menuState.rowNumber !== undefined) {
      const values: Record<string, ICellValue> = {}
      const result = findRow(menuState.rowId, spreadsheetData.rows)

      if (!result) {
        setSnackbarMessage({
          status: 'error',
          message: "This row couldn't be found, so it wasn't duplicated. Refresh the page and try again."
        })
        return
      }
      const row = result

      if (row) {
        for (const column of spreadsheetData.viewDetails.columns) {
          const synced = column.isSynced
          const isHidden = spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId)
          const isReadOnly =
            isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) ||
            column.scriptEnabled ||
            column.formulaEnabled ||
            column.isJoined ||
            synced
          if (isReadOnly || isHidden) continue
          values[column.publicId] = getRowValue(
            row,
            column.publicId,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.columnStore
          )
        }

        onCreateRow(values, menuState.rowId)
      }
    }
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const handleBulkInsertRows = (evt: React.MouseEvent<HTMLDivElement>, numberRows: number) => {
    evt.preventDefault()
    const groupSettings: ITableViewGroup[] =
      spreadsheetData.userConfiguration.groupSettings.length > 0
        ? spreadsheetData.userConfiguration.groupSettings
        : spreadsheetData.viewDetails.groupSettings
    if (groupSettings.length > 0 && menuState.rowId !== undefined) {
      const values = getRowIndexAndValues(true)
      onCreateBlankRows(numberRows, values)
    } else {
      onCreateBlankRows(numberRows)
    }
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const allowInsert = spreadsheetData.isContributor
  const plural = multiCellSelection ? 's' : ''
  const canAddRows =
    !spreadsheetData.viewDetails.disableNewRow &&
    !spreadsheetData.tableDetails.isSyncing &&
    !hasSyncStartedThisSession(spreadsheetData.tableDetails.publicId)
  const canDelete =
    rowCapabilities.deleteRow &&
    !spreadsheetData.tableDetails.isSynced &&
    (spreadsheetData.isAdmin || (spreadsheetData.isContributor && spreadsheetData.viewDetails.allowContributorDelete))
  const needsContributor = (action: string) =>
    allowInsert ? undefined : `You need contributor permissions to ${action}`
  // Pressing a row keeps the grid's selection: the clipboard rows act on it.
  const keepSelection = (event: React.MouseEvent) => event.stopPropagation()

  const copyCellId = () => {
    const cellId =
      '{{table/' +
      spreadsheetData.tableDetails.publicId +
      '/' +
      // Menus opened from a row number carry no column of their own, so fall back to the active cell's.
      (menuState.columnId || selectedCell.columnId) +
      '/' +
      menuState.rowId +
      '}}'
    navigator.clipboard.writeText(cellId)
    setSnackbarMessage({ status: 'success', message: 'Cell ID succesfully copied.' })
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const expandRow = () => {
    const rowId = menuState.rowId
    const rowNumber = menuState.rowNumber
    if (rowId !== undefined && rowId !== '' && rowNumber !== undefined) {
      setSearchParams(
        (params) => {
          params.set('row', rowId)
          return params
        },
        { replace: true }
      )
      setExpandModal!(true, false, rowId, rowNumber)
    }
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      handlePasteText(text)
      setMenuState({ ...menuState, open: false })
    } catch {
      setSnackbarMessage({
        status: 'error',
        message:
          "Morta couldn't read your clipboard. Allow clipboard access in your browser, or paste with Ctrl+V (Cmd+V on a Mac)."
      })
    }
  }

  // The menu in groups, a line between each: the clipboard, the row, adding rows, deleting.
  const sections: React.ReactNode[][] = [
    [
      <MenuItem
        key="copy"
        id={`${id}-copy`}
        icon={<Copy />}
        shortcut={`${MOD_KEY}C`}
        onMouseDown={keepSelection}
        onClick={() => {
          document.execCommand('copy')
          setMenuState({ ...menuState, open: false })
        }}
      >
        {`Copy value${plural}`}
      </MenuItem>,
      <MenuItem
        key="cut"
        id={`${id}-cut`}
        icon={<Cut />}
        shortcut={`${MOD_KEY}X`}
        disabledReason={needsContributor('cut values')}
        onMouseDown={keepSelection}
        onClick={async () => {
          await cutSelectedCells()
          setMenuState({ ...menuState, open: false })
        }}
      >
        {`Cut value${plural}`}
      </MenuItem>,
      <MenuItem
        key="paste"
        id={`${id}-paste`}
        icon={<Paste />}
        shortcut={`${MOD_KEY}V`}
        disabledReason={needsContributor('paste values')}
        onMouseDown={keepSelection}
        onClick={paste}
      >
        {`Paste value${plural}`}
      </MenuItem>
    ],
    !multiCellSelection && rowCapabilities.expandRow
      ? [
          <MenuItem key="expand" icon={<Expand />} onClick={expandRow}>
            {spreadsheetData.tableDetails.allowComments ? 'Add comment / expand row' : 'Expand row'}
          </MenuItem>,
          <MenuItem key="cell-id" icon={<CopyLink />} onClick={copyCellId}>
            Copy cell ID
          </MenuItem>,
          <MenuItem
            key="audit"
            icon={<Audit />}
            disabledReason={
              spreadsheetData.isAdmin ? undefined : 'You need admin permissions to view the row audit trail'
            }
            onClick={() => {
              if (menuState.rowId !== undefined) setRowAuditModal(true, menuState.rowId)
            }}
          >
            View row audit trail
          </MenuItem>
        ]
      : [],
    !multiCellSelection && rowCapabilities.expandRow && canAddRows
      ? [
          <MenuItem
            key="insert"
            icon={<Arrow type="up" />}
            disabledReason={needsContributor('add rows')}
            onClick={(event) => handleInsertRowAbove(event as React.MouseEvent<HTMLDivElement>)}
          >
            Insert row above
          </MenuItem>,
          <MenuItem
            key="duplicate"
            icon={<Copy />}
            onClick={(event) => handleDuplicateRow(event as React.MouseEvent<HTMLDivElement>)}
          >
            Duplicate row
          </MenuItem>,
          ...[10, 100].map((count) => (
            <MenuItem
              key={`add-${count}`}
              icon={<Plus />}
              disabledReason={needsContributor('add rows')}
              onClick={(event) => handleBulkInsertRows(event as React.MouseEvent<HTMLDivElement>, count)}
            >
              {`Add ${count} rows to end`}
            </MenuItem>
          ))
        ]
      : [],
    canDelete
      ? [
          <MenuItem
            key="delete"
            icon={<Delete />}
            variant="destructive"
            onMouseDown={keepSelection}
            onClick={() => setDeleteRowModal(true)}
          >
            {`Delete row${plural}`}
          </MenuItem>
        ]
      : []
  ]

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <MenuList>
        {/* The grid keeps its selection while the menu is used: its rows act on it. */}
        <div
          onMouseDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {selectedRowCount > 1 && (
            <MenuLabel data-testid="row-selection-count">{selectedRowCount} rows selected</MenuLabel>
          )}
          {sections
            .filter((rows) => rows.length > 0)
            .map((rows, index) => (
              <React.Fragment key={index}>
                {index > 0 && <MenuSeparator />}
                {rows}
              </React.Fragment>
            ))}
        </div>
      </MenuList>
    </Menu>
  )
}

export default CellMenu
